/**
 * Public read-only snapshot of an investigation verdict — accessed via
 * a share token URL like /snapshot/investigation/<token>. No login
 * required. Only exposes the redacted payload the backend returns.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sparkles, Clock, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

export default function InvestigationSnapshotPage() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getInvestigationSnapshot(token)
      .then((r) => setSnap(r))
      .catch((e) => setErr(e.message || 'Snapshot not found or revoked'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="snapshot-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (err || !snap) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="snapshot-error">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
          <p className="text-sm text-foreground">{err || 'Snapshot not found'}</p>
          <p className="text-xs text-muted-foreground mt-2">The link may have been revoked or has expired.</p>
        </div>
      </div>
    );
  }

  const v = snap.verdict || {};
  const target = snap.target || {};
  const created = snap.created_at ? new Date(snap.created_at).toLocaleString() : '';
  const shared = snap.shared_at ? new Date(snap.shared_at).toLocaleDateString() : '';

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="snapshot-page">
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        {/* Brand header */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-indigo-400" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">INZARA</p>
            <p className="text-xs font-heading font-semibold">Investigation Snapshot</p>
          </div>
          <div className="ml-auto text-[10px] text-muted-foreground text-right">
            {shared && <p>Shared {shared}</p>}
            <p className="uppercase tracking-wider">Read-only</p>
            <a
              href={api.investigationSnapshotPngUrl(token)}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-1 text-[10px] text-indigo-300 hover:text-indigo-200 underline"
              data-testid="snapshot-download-png"
            >
              Download PNG
            </a>
          </div>
        </div>

        {/* Question */}
        <div className="rounded-xl border border-border/50 bg-card/40 p-5 mb-4" data-testid="snapshot-question">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Question</p>
          <p className="text-base font-semibold text-foreground">{snap.question || 'Untitled investigation'}</p>
          <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-muted-foreground">
            {target.metric && <span>Metric: <span className="text-foreground/90 font-mono">{target.metric}</span></span>}
            {target.model_name && <span>Model: <span className="text-foreground/90">{target.model_name}</span></span>}
            {created && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{created}</span>}
          </div>
        </div>

        {/* Verdict */}
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 mb-4" data-testid="snapshot-verdict">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Verdict</p>
          </div>
          <p className="text-base text-foreground/95 leading-relaxed" data-testid="snapshot-verdict-text">
            {v.verdict || '(No summary produced)'}
          </p>
        </div>

        {/* Top drivers */}
        {(v.top_drivers || []).length > 0 && (
          <div className="rounded-xl border border-border/50 bg-card/40 p-5 mb-4" data-testid="snapshot-drivers">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Top drivers</p>
            <ol className="space-y-2">
              {v.top_drivers.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" data-testid={`snapshot-driver-${i}`}>
                  <span className="text-[10px] font-mono text-muted-foreground pt-1">{i + 1}.</span>
                  <div className="flex-1">
                    <p className="text-foreground/95">{d.statement}</p>
                    {typeof d.confidence === 'number' && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Confidence: {Math.round(d.confidence * 100)}%</p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Follow-ups */}
        {(v.follow_ups || []).length > 0 && (
          <div className="rounded-xl border border-border/50 bg-card/40 p-5 mb-4" data-testid="snapshot-followups">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Suggested follow-ups</p>
            <ul className="space-y-1.5 list-disc list-inside">
              {v.follow_ups.map((f, i) => (
                <li key={i} className="text-sm text-foreground/95" data-testid={`snapshot-followup-${i}`}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Caveats */}
        {(v.caveats || []).length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4" data-testid="snapshot-caveats">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <p className="text-[10px] uppercase tracking-wider text-amber-300">Caveats</p>
            </div>
            <ul className="space-y-1 list-disc list-inside text-[12px] text-amber-100/80">
              {v.caveats.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground text-center mt-8">
          Read-only snapshot · Full evidence and history stay inside INZARA
        </p>
      </div>
    </div>
  );
}
